import { fetchAuthSession, getCurrentUser, signIn, signInWithRedirect, signOut, updatePassword } from "@aws-amplify/auth";
import { Amplify } from "aws-amplify";
import { getCognitoConfig } from "./CognitoConfig";

const config = getCognitoConfig();
Amplify.configure(config);

/** Redirect to Cognito hosted UI (external login page). */
function isAlreadyAuthenticatedError(error) {
    const name = String(error?.name || "");
    const message = String(error?.message || "");
    return (
        name === "UserAlreadyAuthenticatedException" ||
        /already a signed in user/i.test(message)
    );
}

export const loginWithRedirect = async () => {
    try {
        await signInWithRedirect();
    } catch (error) {
        if (isAlreadyAuthenticatedError(error)) {
            return { alreadyAuthenticated: true };
        }
        console.error("Error during login redirect:", error);
        throw error;
    }
    return { alreadyAuthenticated: false };
};

/** Sign in with username and password (in-app form). Returns { success, nextStep, error }. */
export const loginWithPassword = async (username, password) => {
    try {
        const result = await signIn({ username: username.trim(), password });
        const step = result?.nextStep?.signInStep;
        if (step === "DONE" || step === "COMPLETE_AUTH_SIGN_IN") {
            return { success: true };
        }
        return { success: false, nextStep: step };
    } catch (error) {
        console.error("Error during sign in:", error);
        return { success: false, error };
    }
};

/** @deprecated Use loginWithPassword for in-app form or loginWithRedirect for hosted UI. */
export const login = async () => {
    return loginWithRedirect();
};

/**
 * Same OAuth entry as sign-in (`/oauth2/authorize`). Do not use Cognito `/signup` in the WebView — many pools
 * (e.g. managed login) return a load error there. Users choose “Sign up” on the hosted page.
 */
export const signup = async () => {
    return loginWithRedirect();
};

/** Fired after login/logout so MinimalHeader + UserLayout refresh auth from Amplify (single source of truth). */
export const AUTH_CHANGED_EVENT = "puzzleAuthChanged";

export function notifyAuthChanged() {
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(AUTH_CHANGED_EVENT));
    }
}

export const logout = async () => {
    try {
        try {
            await signOut({ global: true });
        } catch {
            await signOut();
        }
        console.log("Logout successful");
    } catch (error) {
        console.error("Error during logout:", error);
    } finally {
        notifyAuthChanged();
    }
};

export const isAuthenticated = async () => {
    try {
        const session = await fetchAuthSession();
        const isValid = session.tokens?.accessToken?.toString() && !isTokenExpired(session.tokens.accessToken);
        return isValid;
    } catch (error) {
        console.error("Error checking authentication:", error);
        return false;
    }
};

const isTokenExpired = (token) => {
    try {
        const payload = JSON.parse(atob(token.toString().split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        return payload.exp < currentTime;
    } catch (error) {
        console.error("Error checking token expiration:", error);
        return true;
    }
};

export const getUser = async () => {
    try {
        const user = await getCurrentUser();
        return user;
    } catch (error) {
        if (error?.name !== "UserUnAuthenticatedException") {
            console.error("Error getting user:", error);
        }
        return null;
    }
};

export const getToken = async () => {
    try {
        const session = await fetchAuthSession();
        return session.tokens?.accessToken?.toString();
    } catch (error) {
        console.error("Error getting token:", error);
        return null;
    }
};

export const getIdToken = async () => {
    try {
        const session = await fetchAuthSession();
        return session.tokens?.idToken?.toString();
    } catch (error) {
        console.error("Error getting ID token:", error);
        return null;
    }
};

export const getUserGroups = async () => {
    try {
        const session = await fetchAuthSession();
        // Cognito groups are typically in the ID token, not access token
        const idTokenGroups = session.tokens?.idToken?.payload?.["cognito:groups"] || [];
        const accessTokenGroups = session.tokens?.accessToken?.payload?.["cognito:groups"] || [];
        // Combine both to handle cases where groups might be in either token
        const groups = [...idTokenGroups, ...accessTokenGroups].filter((v, i, a) => a.indexOf(v) === i);
        return groups;
    } catch (error) {
        console.error("Error getting user groups:", error);
        return [];
    }
};

export const getUserRoles = async () => {
    try {
        const groups = await getUserGroups();
        return groups.filter(group => group.startsWith('ROLE_') || group.startsWith('APP_'));
    } catch (error) {
        console.error("Error getting user roles:", error);
        return [];
    }
};

export const hasRole = async (role) => {
    try {
        const roles = await getUserRoles();
        return roles.includes(role);
    } catch (error) {
        console.error("Error checking role:", error);
        return false;
    }
};

export const hasAnyRole = async (roles) => {
    try {
        const userRoles = await getUserRoles();
        return roles.some(role => userRoles.includes(role));
    } catch (error) {
        console.error("Error checking roles:", error);
        return false;
    }
};

/**
 * Change password for the signed-in Cognito user (username/password sign-in).
 * @returns {{ success: true } | { success: false, errorKey: string }}
 */
export const updateUserPassword = async (oldPassword, newPassword) => {
    try {
        await updatePassword({ oldPassword, newPassword });
        return { success: true };
    } catch (error) {
        console.error("Error updating password:", error);
        const name = error?.name || "";
        if (name === "NotAuthorizedException") {
            return { success: false, errorKey: "wrong-current" };
        }
        if (name === "InvalidPasswordException") {
            return { success: false, errorKey: "invalid-new" };
        }
        if (name === "LimitExceededException" || name === "TooManyRequestsException") {
            return { success: false, errorKey: "limit" };
        }
        if (name === "PasswordResetRequiredException") {
            return { success: false, errorKey: "password-reset-required" };
        }
        return { success: false, errorKey: "generic" };
    }
};

export const getSessionInfo = async () => {
    try {
        const session = await fetchAuthSession();
        return {
            accessToken: session.tokens?.accessToken?.toString(),
            idToken: session.tokens?.idToken?.toString(),
            refreshToken: session.tokens?.refreshToken?.toString(),
            expiresAt: session.tokens?.accessToken?.payload?.exp,
            groups: session.tokens?.accessToken?.payload?.["cognito:groups"] || []
        };
    } catch (error) {
        console.error("Error getting session info:", error);
        return null;
    }
};

export default {
    login,
    loginWithRedirect,
    loginWithPassword,
    signup,
    logout,
    notifyAuthChanged,
    isAuthenticated,
    getUser,
    getToken,
    getIdToken,
    getUserGroups,
    getUserRoles,
    hasRole,
    hasAnyRole,
    updateUserPassword,
    getSessionInfo
};
